import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearQuery, getNotifyFormula, getQuery, setQuery } from "../../../../../services/local-session-storage";
import {
    queryToConsultation,
    saveConsultation,
    createBatchAestheticConsultation,
    queryToBatchConsultation,
} from "../../../../../services/decisionFlow";
import { useAuth } from "../../../../../contexts/auth2";
import { ProductModel } from "../../../../../models/Product.model";
import { generatePdfForConsultations, generatePdfForBody, openHtmlForPrint } from "../../../../../utils/pdf.util";
import { QueryState } from "../../index";

/** Estado, persistência, geração de receita e navegação da etapa de doses do wizard tópico. */
export function useDosePage() {
    const model = getQuery();
    const navigate = useNavigate();

    const {
        user: { idUser },
    } = useAuth();
    const [open, setOpen] = useState(false);
    const [observation, setObservation] = useState(model.observation || "");
    const [showAnotherConsult, setShowAnotherConsult] = useState(false);
    const [showNotifyDialog, setShowNotifyDialog] = useState<{ show: boolean; action?: "done" | "pdf" }>({ show: false, action: undefined });
    const [notifyWasShown, setNotifyWasShown] = useState(false);
    const [observations, setObservations] = useState<string[]>(model.observations || []);
    const [warning, setWarning] = useState<boolean>(false);
    const [products, setProducts] = useState<ProductModel[]>([]);
    const [openObservations, setOpenObservations] = useState(false);
    const [nameConsultation, setNameConsultation] = useState<string>(model.consultationName || "");
    const [totalPercentage, setTotalPercentage] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [openNoPatientDialog, setOpenNoPatientDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState<"done" | "pdf" | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailProduct, setDetailProduct] = useState<ProductModel | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

    const hasMultiplePatients = model.patientIds && model.patientIds.length > 1;

    const validateProducts = () => {
        for (const product of products) {
            const percentage = Number(product.percentage);
            if (percentage < Number(product.minimumDosage) || percentage > Number(product.maxDosage)) {
                return false;
            }
        }
        return true;
    };

    const handleChange = (id: number, value: string) => {
        const product = products.find((p) => p.idProduct == id);
        if (product) {
            product.percentage = value;
        }

        setWarning(!validateProducts());
        setQuery({ products });

        const sum = products.reduce((acc, p) => acc + Number(p.percentage || 0), 0);
        setTotalPercentage(sum);
    };

    const save = useCallback(async () => {
        try {
            const currentQuery = getQuery();

            if (currentQuery.patientIds && currentQuery.patientIds.length > 0) {
                const batchRequest = queryToBatchConsultation(
                    currentQuery,
                    idUser,
                    observation,
                    observations,
                    products,
                    currentQuery.patientIds,
                    nameConsultation
                );
                const result = await createBatchAestheticConsultation(batchRequest);

                const successIds = result.data.results.filter((r) => r.consultationId !== undefined).map((r) => r.consultationId as number);

                if (result.data.summary.failed > 0) {
                    setSnackbarMessage(`${result.data.summary.created} consultas criadas, ${result.data.summary.failed} falharam`);
                    setShowSnackbar(true);
                }

                return successIds;
            }

            const request = queryToConsultation(currentQuery, idUser, observation, observations, products, nameConsultation);
            const result = await saveConsultation(request);

            if ("consultations" in result.data) {
                return result.data.consultations.map((c) => c.consultationId);
            }

            return [result.data.id] as number[];
        } catch (error: any) {
            setSnackbarMessage("Erro ao salvar a consulta estética");
            setShowSnackbar(true);
            throw error;
        }
    }, [idUser, observation, observations, products, nameConsultation]);

    const openHtmlAndNavigate = (html: string) => {
        openHtmlForPrint(html);

        const hasPatients = !!model.patientId || (!!model.patientIds && model.patientIds.length > 0);
        if (hasPatients) {
            setShowAnotherConsult(true);
        } else {
            setTimeout(() => {
                navigate("/");
            }, 500);
        }
    };

    const handleGeneratePdf = useCallback(
        async (warningFlag: boolean = false) => {
            setLoading(true);
            try {
                if (warningFlag) {
                    setOpen(true);
                    setLoading(false);
                } else if (!!model.patientId || (!!model.patientIds && model.patientIds.length > 0)) {
                    const ids = await save();
                    const result = await generatePdfForConsultations(ids.map((id) => ({ consultationId: id, type: "TOPIC" })));
                    if (result.success) {
                        if (result.html) {
                            openHtmlAndNavigate(result.html);
                        } else {
                            // V2: PDF já foi baixado, apenas navega
                            const hasPatients = !!model.patientId || (!!model.patientIds && model.patientIds.length > 0);
                            if (hasPatients) {
                                setShowAnotherConsult(true);
                            } else {
                                setTimeout(() => navigate("/"), 500);
                            }
                        }
                    }
                    setLoading(false);
                } else {
                    const result = await generatePdfForBody(model, idUser, observation, observations, products, nameConsultation);
                    if (result.success) {
                        if (result.html) {
                            openHtmlAndNavigate(result.html);
                        } else {
                            // V2: PDF já foi baixado, apenas navega
                            setTimeout(() => navigate("/"), 500);
                        }
                    }
                    setLoading(false);
                }
            } catch (error: any) {
                setSnackbarMessage("Erro ao gerar o PDF da consulta");
                setShowSnackbar(true);
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [save, setOpen, model, observation, observations, products, nameConsultation, idUser, navigate]
    );

    const handleDone = async (ignoreWarning = false) => {
        try {
            if (warning && !ignoreWarning) {
                setPendingAction("done");
                setOpen(true);
                return;
            }

            if (!!model.patientId || (!!model.patientIds && model.patientIds.length > 0)) {
                await save();
                setShowAnotherConsult(true);
            } else {
                navigate("/");
            }
        } catch (error: any) {
            setSnackbarMessage("Erro ao finalizar a consulta");
            setShowSnackbar(true);
        }
    };

    const handleActionsToDone = async (action: "done" | "pdf" | undefined, ignorePatientCheck = false) => {
        const hasPatients = !!model.patientId || (!!model.patientIds && model.patientIds.length > 0);

        if (!hasPatients && !ignorePatientCheck) {
            setPendingAction(action);
            setOpenNoPatientDialog(true);
            return;
        }

        if (warning) {
            setPendingAction(action);
            setOpen(true);
            return;
        }

        if (!getNotifyFormula() && !notifyWasShown && hasPatients) {
            setShowNotifyDialog({ show: true, action });
            setNotifyWasShown(true);
            return;
        }

        if (action === "done") {
            await handleDone();
        } else {
            await handleGeneratePdf(warning);
        }
    };

    const handleConfirmDialog = useCallback(
        async () => {
            setOpen(false);
            setLoading(true);
            try {
                if (pendingAction === "done") {
                    await handleDone(true);
                } else if (pendingAction === "pdf") {
                    await handleGeneratePdf(false);
                }
            } finally {
                setPendingAction(undefined);
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pendingAction, handleDone, handleGeneratePdf]
    );

    const onDeleteItem = useCallback(
        (id: number) => {
            setProducts((prev) => prev.filter((product) => product.idProduct !== id));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [products]
    );

    const handleFinishConsult = useCallback(async () => {
        clearQuery();
        setShowAnotherConsult(false);
        navigate("/dashboard/query");
    }, [navigate]);

    const handleAnotherConsult = useCallback(
        async () => {
            clearQuery();
            if (model.patientIds && model.patientIds.length > 0) {
                navigate("/dashboard/query", {
                    state: { patientIds: model.patientIds } as QueryState,
                });
            } else if (model.patientId) {
                navigate("/dashboard/query", {
                    state: { patientId: model.patientId } as QueryState,
                });
            } else {
                navigate("/dashboard/query");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate]
    );

    const handleBackButton = useCallback(() => {
        navigate("/dashboard/query", {
            state: { fromDoseNavigation: true } as QueryState,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Sem estado do wizard (aba nova, "voltar" depois de salvar, link direto): recomeça a consulta.
        if (!model.products?.length) {
            navigate("/dashboard/query", { replace: true });
            return;
        }
        setProducts(model.products.sort((a, b) => (a.showConcentration === b.showConcentration ? 0 : a.showConcentration ? -1 : 1)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setQuery({ products });
    }, [products]);

    useEffect(() => {
        const sum = products.reduce((acc, p) => acc + Number(p.percentage || 0), 0);
        setTotalPercentage(sum);
    }, [products]);

    // Thin wrappers that mirror the original inline setQuery side-effects
    const updateName = (value: string) => {
        setNameConsultation(value);
        setQuery({ consultationName: value });
    };

    const updateObservation = (obs: string) => {
        setObservation(obs);
        setQuery({ observation: obs });
    };

    const saveObservations = (next: string[]) => {
        setOpenObservations(false);
        setObservations(next);
        setQuery({ observations: next });
    };

    return {
        model,
        hasMultiplePatients,
        // dialog / ui state
        open,
        setOpen,
        showAnotherConsult,
        showNotifyDialog,
        setShowNotifyDialog,
        openObservations,
        setOpenObservations,
        previewOpen,
        setPreviewOpen,
        openNoPatientDialog,
        setOpenNoPatientDialog,
        pendingAction,
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        detailOpen,
        setDetailOpen,
        detailProduct,
        setDetailProduct,
        deleteDialogOpen,
        setDeleteDialogOpen,
        pendingDeleteId,
        setPendingDeleteId,
        // data
        observation,
        observations,
        products,
        nameConsultation,
        totalPercentage,
        // handlers
        handleChange,
        handleActionsToDone,
        handleConfirmDialog,
        onDeleteItem,
        handleFinishConsult,
        handleAnotherConsult,
        handleBackButton,
        updateName,
        updateObservation,
        saveObservations,
    };
}
